import ContextVariableAccessor from '@/core/application-dictionary/components/ContextVariableAccessor';
import AccessLevelMixin from '@/core/application-dictionary/mixins/AccessLevelMixin';
import { ADColumnType } from '@/shared/model/ad-column.model';
import { IADField } from '@/shared/model/ad-field.model';
import { Component, Mixins, Vue, Watch } from 'vue-property-decorator';
import { isBooleanField } from '@/utils/validate';

const baseApiReferenceList = 'api/ad-reference-lists';

const AdInputListProps = Vue.extend({
  props: {
    classNames: [Array, Object],
    disabled: Boolean,
    fieldMeta: Object,
    placeholder: String,
    query: {
      type: Array,
      default: () => {
        return [];
      }
    },
    referenceKey: String,
    size: String,
    value: [Boolean, String],
  },
});

@Component
export default class AdInputList extends Mixins(AccessLevelMixin, ContextVariableAccessor, AdInputListProps) {
  
  private validationRule: string = null;

  loading: boolean = false;
  reference: string | boolean = null;
  referenceList: any[] = [];

  get isBoolean() {
    return this.fieldMeta && isBooleanField(this.fieldMeta);
  }

  get isMandatory() {
    return this.fieldMeta && this.fieldMeta.mandatory || this.fieldMeta.adColumn?.mandatory;
  }

  @Watch('value')
  onValueChanged(value?: string | boolean) {
    this.reference =  value;
  }

  onChanged(value?: string | boolean) {
    this.$emit('input', value);
    this.$emit('change', value);
  }

  created() {
    if (this.fieldMeta) {
      const field = (this.fieldMeta as IADField);
      this.tabName = field.adTabName;
      this.initValidationRule();
    } else if (this.referenceKey) {
      this.initRelationships();
    }

    this.onValueChanged(this.value);
  }

  private async initValidationRule() {
    // Parse the validation rule which is used to filter the reference key records.
    const field = this.fieldMeta as IADField;
    const column = field.adColumn;
    const overrideColumn = field.adValidationRule !== void 0 && field.adValidationRule !== null;
    const vRule = overrideColumn ? field.adValidationRule : column?.adValidationRule;

    if (vRule) {
      let referenceFilter = <string>this.getContext({
        defaultTabId: this.tabName,
        text: vRule.query,
      });

      if (referenceFilter?.includes('{select', 0)) {
        this.loading = true;

        try {
          referenceFilter = await this.parseValidationQuery({
            defaultTabId: this.tabName,
            text: referenceFilter,
          });

          // Avoid exception when creating a new record, which will invoke
          // the backend API with a bad request.
          if (referenceFilter && !referenceFilter.includes('=null')) {
            this.validationRule = referenceFilter;
          }
        } catch (err) {
          console.log('Failed initializing validation rule');
        }
      }
    }

    this.initRelationships();
  }

  private async initRelationships() {
    this.loading = true;
    let baseQuery = [];

    if (this.fieldMeta) {
      const field = this.fieldMeta as IADField;
      const column = field.adColumn;
      const referenceId = field.adReference?.id || column?.adReference?.id;

      baseQuery = [`adReferenceId.equals=${referenceId}`, this.validationRule];
    } else if (this.referenceKey) {
      baseQuery = [`adReferenceValue.equals=${this.referenceKey}`, ...this.query];
    }

    this.dynamicWindowService(baseApiReferenceList)
      .retrieve({
        criteriaQuery: ['active.equals=true', ...baseQuery],
      })
      .then(res => {
        const list = res.data.map((item: any) => {
          if (this.isBoolean) {
            item.value = ['true', 'Y'].includes(item.value);
          }
          return item;
        });

        if (this.isBoolean && !this.isMandatory) {
          this.referenceList = [
            { id: 0, name: '', value: null },
            ...list
          ]
        } else {
          this.referenceList = list;
        }
      })
      .catch(err => console.warn('Failed initializing field relationships.', err))
      .finally(() => (this.loading = false));
  }
}